<template>
  <div id="diffWrapper" class="container">
    <template v-if="dossiers.from && dossiers.to">
      <div class="columns">
        <div class="column is-two-thirds">
          <div class="buttons has-addons">
            <button class="button" :class="{ 'is-info': mode === 'manual' }" @click="switchMode">Handmatig per werkproces / kerntaak</button>
            <button class="button" :class="{ 'is-info': mode === 'auto' }" @click="switchMode">Automatisch</button>
          </div>
        </div>

        <div class="column">
          <div class="control is-pulled-right">
            <button
              class="button is-info"
              @click="reset">

              Een vergelijking maken tussen andere dossiers
            </button>
          </div>
        </div>
      </div>

      <template v-if="mode === 'manual'">
        <div class="columns">
          <item-selector
            mode="from"
            :selected="manualCompare.from ? manualCompare.from.nr : ''"
            :items="pairs.map(pair => pair.from)"
            @select="setCompare">
          </item-selector>

          <item-selector
            mode="to"
            :selected="manualCompare.to ? manualCompare.to.nr : ''"
            :items="pairs.map(pair => pair.to)"
            @select="setCompare">
          </item-selector>
        </div>
      </template>

      <template v-else>
        <div class="columns">
          <div class="column">
            <h2 class="title is-5">Verschillen in dossiers tonen</h2>
            <p>van: {{ dossiers.from.nr }} - {{ dossiers.from.name }}</p>
            <p>naar: {{ dossiers.to.nr }} - {{ dossiers.to.name }}</p>
          </div>
        </div>

        <div class="columns">
          <div class="column">
            <label class="checkbox">
              <input type="checkbox" v-model="lineByLine">
              De verschillen onder elkaar tonen in plaats van naast elkaar
            </label>
          </div>
        </div>

        <hr>

        <div
          class="card"
          v-for="(pair, idx) in pairs"
          :key="idx">

          <header class="card-header">
            <p class="card-header-title">{{ (pair.from) ? `${pair.from.hoofdstuk} ${pair.from.titel}` : 'Geen match gevonden' }}</p>
            <p class="card-header-title is-pulled-right">{{ (pair.to) ? `${pair.to.hoofdstuk} ${pair.to.titel}` : 'Geen match gevonden' }}</p>
          </header>

          <div class="card-content">
            <code-diff
              :old-string="getParsed(pair.from, 'from')"
              :new-string="getParsed(pair.to, 'to')"
              :context="10"
              :outputFormat="lineByLine ? 'line-by-line' : 'side-by-side'">
            </code-diff>
          </div>
        </div>
      </template>
    </template>

    <template v-else>
      <div class="field is-grouped">
        <div class="control">
          <label class="label">Van dossier</label>

          <select-wrapper
            :value="from"
            :options="myDossiers"
            @input="updateFrom"
            placeholder="Selecteer een dossier">
          </select-wrapper>
        </div>

        <div class="control">
          <label class="label">Naar dossier</label>

          <select-wrapper
            :value="to"
            :options="myDossiers"
            @input="updateTo"
            placeholder="Selecteer een dossier">
          </select-wrapper>
        </div>
      </div>

      <div class="control">
        <button
          class="button is-info"
          :disabled="!nrsAreSet"
          @click="loadDossiersToCompare">

          Vergelijken
        </button>
      </div>
    </template>

    <edit-modal :caption="manualCompareCaption" :wide="true" :isActive="showManualComparison">
      <code-diff
        v-if="showManualComparison"
        :old-string="getParsed(manualCompare.from, 'from')"
        :new-string="getParsed(manualCompare.to, 'to')"
        :context="10"
        :outputFormat="lineByLine ? 'line-by-line' : 'side-by-side'">
      </code-diff>

      <button class="button" @click="closeManualComparison">Sluiten</button>
    </edit-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import CodeDiff from 'vue-code-diff'
import EditModal from '@/components/modules/EditModal'
import ItemSelector from '@/components/dossiers/ItemSelector'
import SelectWrapper from '@/components/wrappers/SelectWrapper'

export default {
  name: 'Diff',
  components: {
    CodeDiff,
    EditModal,
    ItemSelector,
    SelectWrapper
  },
  data () {
    return {
      to: null,
      from: null,
      mode: 'manual',
      lineByLine: false,
      manualCompare: {
        from: null,
        to: null
      },
      parsers: {
        wp: this.parseWp,
        kv: this.parseKv
      }
    }
  },
  computed: {
    ...mapGetters({
      dossiers: 'dossiers/dossiers'
    }),
    myDossiers () {
      return this.dossiers.map(dossier => {
        return {
          label: `${dossier.nr} - ${dossier.titel} (${dossier.nrvoorganger})`,
          value: parseInt(dossier.nr)
        }
      })
    },
    pairs () {
      if (this.dossiers.from && this.dossiers.to) {
        const wpsLeft = this.dossiers.from.werkprocessen
        const wpsRight = this.dossiers.to.werkprocessen
        const kvsLeft = this.dossiers.from.kennisenvaardigheden
        const kvsRight = this.dossiers.to.kennisenvaardigheden

        return wpsLeft.map(wp => this.getChanges(wp, wpsRight, 'wp'))
          .concat(this.mergeAdditions(wpsRight, wpsLeft, 'wp'))
          .concat(kvsLeft.map(kv => this.getChanges(kv, kvsRight, 'kv'))
            .concat(this.mergeAdditions(kvsRight, kvsLeft, 'kv')))
      }

      return []
    },
    nrsAreSet () {
      return this.from !== null && this.to !== null
    },
    showManualComparison () {
      return this.manualCompare.from !== null && this.manualCompare.to !== null
    },
    manualCompareCaption () {
      if (!this.showManualComparison) return ''

      return `Vergelijking van ${this.manualCompare.from.hoofdstuk} - ${this.manualCompare.from.titel} naar ${this.manualCompare.to.hoofdstuk} - ${this.manualCompare.to.titel}`
    }
  },
  created () {
    if (this.$attrs.dossiers) {
      const dossiers = this.$attrs.dossiers.split(':')

      this.from = parseInt(dossiers[0])
      this.to = parseInt(dossiers[1])
      this.loadDossiersToCompare()
    } else {
      this.$store.dispatch('dossiers/loadAll')
    }
  },
  methods: {
    switchMode () {
      this.mode = (this.mode === 'manual') ? 'auto' : 'manual'
    },
    isWp (id) {
      return id.indexOf('W') > -1
    },
    isKv (id) {
      return id.indexOf('W') === -1
    },
    getType (id) {
      return (this.isWp(id)) ? 'wp' : 'kv'
    },
    loadDossiersToCompare () {
      this.$store.dispatch('dossiers/loadDiff', { from: this.from, to: this.to })
    },
    updateFrom (payload) {
      this.from = payload
    },
    updateTo (payload) {
      this.to = payload
    },
    reset () {
      this.from = this.to = null
      this.$store.dispatch('dossiers/loadAll')
    },
    getParsed (item, mode) {
      return (item) ? this.parsers[this.getType(item.hoofdstuk)](item, this.dossiers[mode].profielen) : ''
    },
    parseWp (wp, profiles) {
      return `======================= Hoofdstuk =========================
${wp.hoofdstuk}
======================= Hoofdstuk =========================

========================= Titel ===========================
${wp.titel}
========================= Titel ===========================

====================== Omschrijving =======================
${wp.omschrijving.algemeen}
${this.getProfileData(wp.omschrijving.aanvullend, profiles)}
====================== Omschrijving =======================

========================== Gedrag =========================
${wp.gedrag.algemeen}
${this.getProfileData(wp.gedrag.aanvullend, profiles)}
========================== Gedrag =========================

======================== Resultaat ========================
${wp.resultaat.algemeen}
${this.getProfileData(wp.resultaat.aanvullend, profiles)}
======================== Resultaat ========================
`
    },
    parseKv (kv) {
      return `======================= Hoofdstuk =========================
${kv.hoofdstuk}
======================= Hoofdstuk =========================

========================= Titel ===========================
${kv.titel}
========================= Titel ===========================

======================= Algemeen ==========================
${this.listKv(kv.refs.algemeen)}
======================= Algemeen ==========================

======================= Per categorie ==========================
${this.listKv(kv.refs['per-categorie'])}
======================= Per categorie ==========================

======================= Per profiel ==========================
${this.listKv(kv.refs['per-profiel'])}
======================= Per profiel ==========================
`
    },
    getProfileData (data, profiles) {
      return data.map(profile => {
        return `
${profiles.find(x => x.nr === profile.referentie).titel}
${profile.omschrijving}`
      }).join(`
`)
    },
    listKv (kvs) {
      return kvs.map(kv => kv.titel).join(`
`)
    },
    getItemNrs (items) {
      return items.map(wp => {
        return {
          nr: wp.nr,
          nrvoorganger: wp.nrvoorganger
        }
      })
    },
    setCompare (payload) {
      this.manualCompare[payload.mode] = payload.item
    },
    getChanges (item, itemsOnTheRight) {
      const matchesOnTheRight = itemsOnTheRight.find(x => x.nrvoorganger === item.nr)

      return {
        from: item,
        to: matchesOnTheRight
      }
    },
    mergeAdditions (itemsOnTheRight, itemsOnTheLeft) {
      return itemsOnTheRight.filter(x => typeof itemsOnTheLeft.find(y => y.nr === x.nrvoorganger) === 'undefined').map(item => {
        return {
          from: null,
          to: item
        }
      })
    },
    closeManualComparison () {
      this.manualCompare.from = this.manualCompare.to = null
    }
  }
}
</script>

<style lang="css">
.card {
  margin-bottom: 15px;
}
</style>
